import { useEffect, useState, useCallback, Fragment } from 'react';
import RetailerLayout from './RetailerLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { retailer, getErrorMessage } from '../../utils/api';

const _money = (n) => `$${Number(n || 0).toFixed(2)}`;

function _download(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
}

export default function RetailerReconciliation({ user, onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await retailer.listReconciliation();
      setLogs(res.data.reconciliations || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load reconciliation logs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detail[id]) {
      try {
        const res = await retailer.getReconciliation(id);
        setDetail((d) => ({ ...d, [id]: res.data.reconciliation }));
      } catch (e) { toast.error(getErrorMessage(e, 'Failed to load detail')); }
    }
  };

  const doDownload = async (log, format) => {
    try {
      const res = await retailer.downloadReconciliation(log.id, format);
      _download(res.data, `reconciliation-${log.period_type}-${log.period_label}.${format}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Download failed'));
    }
  };

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="retailer-reconciliation-page">
        <div className="bg-gradient-to-r from-slate-900 to-teal-900 rounded-2xl p-8 text-white shadow-xl flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <FileText className="w-7 h-7" /> Reconciliation Center
            </h1>
            <p className="text-teal-100 mt-1 text-sm max-w-2xl">
              Daily &amp; monthly logs of the sales tax DealShaq collected and remits to Virginia on your
              facilitated orders — broken down by SKU, category, and locality for your audits.
            </p>
          </div>
          <Button variant="secondary" onClick={load} data-testid="retailer-recon-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your Reconciliation Logs</CardTitle>
            <CardDescription>DealShaq is the registered Virginia marketplace facilitator — you do not remit this tax.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
            ) : logs.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="retailer-recon-empty">
                No reconciliation logs yet. They are generated automatically as your facilitated orders settle.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead />
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Gross Sales</TableHead>
                    <TableHead className="text-right">Tax Collected</TableHead>
                    <TableHead className="text-right">Refunds</TableHead>
                    <TableHead className="text-right">Download</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <Fragment key={log.id}>
                      <TableRow data-testid={`retailer-recon-row-${log.id}`}>
                        <TableCell>
                          <button onClick={() => toggle(log.id)} data-testid={`retailer-recon-toggle-${log.id}`}>
                            {expanded === log.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium capitalize">{log.period_type} · {log.period_label}</TableCell>
                        <TableCell className="text-right">{_money(log.summary?.gross_sales)}</TableCell>
                        <TableCell className="text-right">{_money(log.summary?.tax_collected)}</TableCell>
                        <TableCell className="text-right">{_money(log.summary?.refund_total)}</TableCell>
                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="outline" onClick={() => doDownload(log, 'csv')} data-testid={`retailer-recon-csv-${log.id}`}><Download className="w-3 h-3 mr-1" />CSV</Button>
                          <Button size="sm" variant="outline" onClick={() => doDownload(log, 'pdf')} data-testid={`retailer-recon-pdf-${log.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
                        </TableCell>
                      </TableRow>
                      {expanded === log.id && detail[log.id] && (
                        <TableRow key={`${log.id}-detail`}>
                          <TableCell colSpan={6} className="bg-gray-50">
                            <div className="grid md:grid-cols-2 gap-6 py-2">
                              <div>
                                <h4 className="font-semibold text-sm mb-2">By Tax Category</h4>
                                <Table><TableBody>
                                  {(detail[log.id].category_breakdown || []).map((c) => (
                                    <TableRow key={c.category_name}><TableCell>{c.category_name}</TableCell><TableCell className="text-right">{_money(c.sales_in_category)}</TableCell><TableCell className="text-right">{_money(c.tax_collected_in_category)}</TableCell></TableRow>
                                  ))}
                                </TableBody></Table>
                              </div>
                              <div>
                                <h4 className="font-semibold text-sm mb-2">Transactions</h4>
                                <Table><TableBody>
                                  {(detail[log.id].transactions || []).slice(0, 12).map((t, i) => (
                                    <TableRow key={i}><TableCell className="text-xs">{t.item_name || t.sku}</TableCell><TableCell className="text-right">×{t.quantity}</TableCell><TableCell className="text-right">{_money(t.tax_amount)}</TableCell></TableRow>
                                  ))}
                                </TableBody></Table>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </RetailerLayout>
  );
}
